import React, { useCallback, useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";

const LottieLoader = dynamic(
  () =>
    import("@/components/Molecules/FullScreenLoader").then(
      (module) => module.LottieLoader
    ),
  { ssr: false }
);

import {
  Button,
  DesktopContainer,
  ErrorUI,
  MobileContainer,
  SVGSprite,
} from "@/components/Atoms";
import DateCarousel from "./DateCarousel";
import SessionDetailModal from "./SessionDetailModal";
import SessionCard from "./SessionCard";
import PaymentModal from "@/containers/ShyftPassSingleClassPage/PaymentModal";
import WaitListModal from "@/containers/ShyftPassBooking/ScheduleSection/SessionCard/WaitListModal";

import { useAppDispatch, useAppSelector } from "@/hooks";
import { clsx } from "@/utils";

import * as actions from "@/redux/ShyftPassReducer";

import mapWorkoutScheduleData from "./mapWorkoutScheduleData";

import { WorkoutScheduleSectionDataI } from "../shyftPassMaps";

import styles from "./WorkoutScheduleSection.module.scss";
import ZoneButtonGroup from "./ZoneButtonGroup";

interface WorkoutScheduleSectionProps extends WorkoutScheduleSectionDataI {}

const WorkoutScheduleSection: React.FC<WorkoutScheduleSectionProps> = ({
  title,
  description,
  isHomePage,
  isSingleClassPurchasePage,
  isRenewalPage,
}) => {
  const dispatch = useAppDispatch();

  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openWaitListModal, setOpenWaitListModal] = useState(false);

  const {
    isLoading,
    shyftPassScheduleData: data,
    selectedDate,
    packages,
    shyftPassZoneList,
    selectedShyftPassZoneId,
  } = useAppSelector((state) => state.shyftPass);

  const { eventDates, centerDetails } = useMemo(() => {
    if (!Object.keys(data)?.length) return {};
    return mapWorkoutScheduleData(data);
  }, [data]);

  const getShyftPassScheduleData = useCallback(
    () =>
      dispatch(
        actions.getShyftPassScheduleData({
          startDate: selectedDate,
          zoneId: selectedShyftPassZoneId,
        })
      ),
    [dispatch, selectedDate, selectedShyftPassZoneId]
  );

  useEffect(() => {
    getShyftPassScheduleData();
  }, [selectedDate,selectedShyftPassZoneId]);

  useEffect(() => {
    if (centerDetails?.length === 0) {
      dispatch(
        actions.getShyftPassScheduleData({
          zoneId: selectedShyftPassZoneId,
        })
      );
    }
  }, []);

  const sectionHeading = (
    <div className={styles.sectionHeading}>
      <h2 className={styles.title}>{title}</h2>
      <p
        className={styles.description}
        dangerouslySetInnerHTML={{ __html: description }}
      />
    </div>
  );

  const arrowRight = (
    <SVGSprite
      className="icon"
      icon="icon-arrow-right-book-slot"
      height="24px"
      width="24px"
      style={{ marginLeft: "4px" }}
    />
  );

  const arrowIcon = (
    <SVGSprite
      className="icon"
      icon="icon-arrow-right-book-slot"
      height="24px"
      width="24px"
    />
  );

  const successIcon = (
    <SVGSprite
      className="icon"
      icon="icon-arrow-right-success"
      height="24px"
      width="24px"
      fill="#fff"
    />
  );

  const renderButton = (data = {}) => {
    const {
      classDetail,
      isWaitListEnabled,
      isWaitListJoined,
      allSlotsBookedMessage,
    } = data;

    let button;
    if (classDetail?.hasAllSlotsBooked) {
      if (isWaitListEnabled) {
        const errorMessage = (
          <ErrorUI
            error={allSlotsBookedMessage || "All slots are booked!"}
            isNotAbsolute
            iconDimension="18px"
            fontSize="14px"
            lineHeight="24px"
          />
        );
        button = (
          <div className="wait-list-wrapper">
            <MobileContainer>{errorMessage}</MobileContainer>
            {isWaitListJoined ? (
              <Button variant={"primary1"} TextColor="#32D583">
                Added to waitlist
                {successIcon}
              </Button>
            ) : (
              <Button
                variant={"primary1"}
                onClick={() => setOpenWaitListModal(data)}
              >
                Join the waitlist
                {arrowIcon}
              </Button>
            )}
            <DesktopContainer>{errorMessage}</DesktopContainer>
          </div>
        );
      } else {
        button = (
          <Button variant={"primary1"} className="all-slots-booked" disabled>
            All slots are booked
          </Button>
        );
      }
    } else if (isHomePage) {
      button = (
        <Button variant="primary1" onClick={() => setOpenPaymentModal(data)}>
          Book your slot for ₹ {data.price || packages?.[0]?.price}
          {arrowRight}
        </Button>
      );
    } else if (isSingleClassPurchasePage || isRenewalPage) {
      button = (
        <Button variant="primary1" onClick={() => setOpenPaymentModal(data)}>
          Book your slot for ₹ {data.price || packages?.[0]?.price}
          {arrowRight}
        </Button>
      );
    }

    return <div className={styles.purchaseButton}>{button}</div>;
  };

  const sectionContent = (
    <div className={styles.sectionContent}>
      {shyftPassZoneList?.length > 1 && (
        <ZoneButtonGroup zoneList={shyftPassZoneList} />
      )}
      <DateCarousel eventDates={eventDates} selectedDate={selectedDate} />
      <div className={styles.sessionList}>
        {isLoading ? (
          <div className={styles.loaderWrapper}>
            <LottieLoader />
          </div>
        ) : (
          centerDetails?.map((data, index) => (
            <div
              key={index}
              className={clsx(
                styles.sessionCardWrapper,
                isHomePage ? styles.homepage : "",
                isLoading ? styles.fadeOut : ""
              )}
            >
              <div className={styles.sessionCard}>
                <SessionCard
                  setOpenDetailModal={setOpenDetailModal}
                  {...data}
                />
              </div>
              {isHomePage && renderButton(data)}
              {(isSingleClassPurchasePage || isRenewalPage) && (
                <DesktopContainer>{renderButton(data)}</DesktopContainer>
              )}
            </div>
          ))
        )}
        {openDetailModal && (
          <SessionDetailModal
            openDetailModal={openDetailModal}
            setOpenDetailModal={setOpenDetailModal}
            isSingleClassPurchasePage={isSingleClassPurchasePage}
            isRenewalPage={isRenewalPage}
            setOpenPaymentModal={setOpenPaymentModal}
            setOpenWaitListModal={setOpenWaitListModal}
          />
        )}
      </div>
    </div>
  );

  return (
    <div
      id="shyft-pass-workout-schedule"
      className={clsx("container", styles.workoutScheduleSection)}
    >
      {sectionHeading}
      {sectionContent}

      {openPaymentModal && (
        <PaymentModal
          openPaymentModal={openPaymentModal}
          setOpenPaymentModal={setOpenPaymentModal}
          isSingleClassPurchasePage={isSingleClassPurchasePage}
          hideZoneDropdown={true}
        />
      )}
      {openWaitListModal && (
        <WaitListModal
          openWaitListModal={openWaitListModal}
          setOpenWaitListModal={setOpenWaitListModal}
        />
      )}
    </div>
  );
};
export default WorkoutScheduleSection;
